/**
 * Terms of Service.
 *
 * The commercial clauses describe the billing the code actually implements:
 * subscription tiers with a 14-day trial (90 days for pre-subscribers),
 * one-off single-trip plans that expire and downgrade, cancellation at the end
 * of the paid period, and - since Stripe Managed Payments - Stripe's
 * subsidiary as the seller of record (legalDetails.reseller). The 14-day
 * withdrawal in section 5 is the button on the billing page
 * (backend withdrawal.service.ts). If the billing changes, this document
 * changes with it.
 *
 * Governing law follows the operator: one US company, Centrafox Consulting
 * LLC, provides the service to every customer. New Mexico is taken from its registered
 * address — if it is incorporated in another state, section 11 and
 * legalDetails.stateOfIncorporation move together.
 */

import { LEGAL } from "./legalDetails";
import { LegalList, LegalPageLayout, LegalSection } from "./LegalPageLayout";

export function TermsPage() {
  const { operator, contact, reseller } = LEGAL;
  const link = (href: string, label: string) => (
    <a
      className="text-primary-600 hover:underline"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </a>
  );

  return (
    <LegalPageLayout
      title="Terms of Service"
      subtitle="The agreement between you and us about using HealthPass."
      description="HealthPass terms of service: the service, accounts, subscriptions and trials, cancellation and refunds, acceptable use, liability and applicable law."
      path="/terms"
    >
      <LegalSection number={1} heading="Who these terms are between">
        <p>
          These terms govern your use of HealthPass, operated by {operator.name}
          , {operator.street}, {operator.city}, {operator.country}{" "}
          (&ldquo;we&rdquo;). We provide the service to you, wherever you live,
          and we are responsible for it.
        </p>
        <p>
          When you buy a paid plan, you buy it from our reseller,{" "}
          {reseller.name}, a Stripe company shown at checkout as &ldquo;
          {reseller.brand}&rdquo;, which acts as merchant of record. It sells
          you the plan, takes payment, issues your invoice and charges the sales
          tax or VAT that applies. Its{" "}
          {link(reseller.termsUrl, "\u201cSold through Link\u201d terms")}{" "}
          govern that purchase; these terms govern everything else.
        </p>
        <p>
          Creating an account means you accept these terms. If you use
          HealthPass for an organisation, you confirm you may bind it.
        </p>
      </LegalSection>

      <LegalSection number={2} heading="What HealthPass is — and is not">
        <p>
          HealthPass stores health information you enter or import, plans trips
          against destination health requirements, and lets you share an
          emergency card. It is a record-keeping and information tool.
        </p>
        <p>
          <strong>
            It is not a medical device, it does not diagnose, treat or advise,
            and it is not an emergency service.
          </strong>{" "}
          Destination requirements, recommendations and anything the AI
          assistant says are informational and may be incomplete or out of date;
          check with a doctor, a travel clinic, or the official authority before
          you rely on them. In an emergency, call your local emergency number.
        </p>
        <p>
          You are responsible for what you enter. We cannot verify that a record
          is accurate, and an emergency responder reading your card sees what
          you put there.
        </p>
        <p>
          <strong>Partner offers.</strong> HealthPass may show you products
          offered by our partners, such as travel health insurance, repatriation
          cover or trip cancellation cover. Each offer names the partner who
          provides it. If you follow an offer, you leave HealthPass for the
          partner&rsquo;s own website, and any contract you conclude there is
          between you and that partner alone, on the partner&rsquo;s terms. We
          are not a party to that contract, we do not advise you on it, compare
          offers or recommend one, and we cannot answer questions about cover
          &mdash; please ask the partner. We are paid a fee by the partner when
          you take out a contract or, for travellers resident in the United
          States, when you visit the partner&rsquo;s website. You can hide all
          partner offers in Settings.
        </p>
      </LegalSection>

      <LegalSection number={3} heading="Your account">
        <LegalList
          items={[
            "You must be 16 or older, and give accurate registration details.",
            "Keep your password to yourself; you are responsible for what happens under your account until you tell us it has been compromised.",
            "One account per person. Do not share credentials — share the emergency card instead, which is what it is for.",
            "Tell us promptly at the support address below if you suspect unauthorised access.",
          ]}
        />
      </LegalSection>

      <LegalSection number={4} heading="Plans, trials and prices">
        <LegalList
          items={[
            <>
              A free tier exists. <strong>Plus</strong> and{" "}
              <strong>Premium</strong> are paid subscriptions, billed monthly in
              advance at the price shown on the pricing page at the time you
              subscribe. Prices include VAT where it applies.
            </>,
            <>
              Plus and Premium start with a <strong>14-day free trial</strong>,
              once per traveller (90 days for travellers who registered by 31
              May 2026). Cancel before the trial ends and you are not charged.
              Otherwise the subscription begins automatically at the end of the
              trial, and we email you three days before.
            </>,
            <>
              <strong>Single-trip plans</strong> are one-off purchases that give
              paid features for a fixed period. They do not renew: when the
              period ends, the account returns to the free tier automatically.
            </>,
            <>
              Payment is taken by our reseller (section 1). We never receive
              your card details. When you subscribe, you authorise it to charge
              the plan price at the start of every billing period until you
              cancel. Your statement shows the charge as &ldquo;LINK.COM*&rdquo;
              followed by our name.
            </>,
            <>
              If a payment fails we retry it and write to you. Persistent
              non-payment suspends the paid features; your data is not deleted
              because of it.
            </>,
            <>
              We may change prices for future billing periods and will give you
              at least 30 days&rsquo; notice by email. You may cancel before the
              change takes effect.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection number={5} heading="Cancellation, withdrawal and refunds">
        <p>
          <strong>Cancelling.</strong> You can cancel at any time in your
          subscription settings. Your plan then runs to the end of the period
          already paid for and does not renew. Outside the withdrawal period
          below, we do not refund part of a period.
        </p>
        <p>
          <strong>Your 14-day right of withdrawal.</strong> As a consumer in the
          EU, the EEA or the UK &mdash; and, as our own policy, wherever you
          live &mdash; you may withdraw from a paid plan within 14 days of
          buying it, without giving a reason. Use the &ldquo;Withdraw from
          contract&rdquo; button on the billing page, or write to{" "}
          {contact.general}. Because you asked for the plan to start straight
          away, you pay only for the days you used; the rest is refunded within
          14 days to the payment method you used. During a free trial nothing
          has been charged, so withdrawing simply ends the plan.
        </p>
        <p>
          <strong>Refunds</strong> go back to your original payment method. For
          a question about a charge you can also contact our reseller&rsquo;s{" "}
          {link(reseller.supportUrl, "Link support")}.
        </p>
      </LegalSection>

      <LegalSection number={6} heading="Acceptable use">
        <LegalList
          items={[
            "Do not upload health data about another person without their consent, or a legal right to act for them.",
            "Do not attempt to access another user's records, probe or overload the service, or work around the entitlement checks on paid features.",
            "Do not upload malware, illegal content, or anything you have no right to share.",
            "Do not scrape the service or resell access to it.",
          ]}
        />
        <p>
          We may suspend an account that breaks these rules, and will tell you
          why unless the law prevents it.
        </p>
      </LegalSection>

      <LegalSection number={7} heading="Your data">
        <p>
          Your health data stays yours. We process it as set out in the Privacy
          Policy, which forms part of these terms. You can export everything in
          a machine-readable format at any time, and you can delete your account
          from your settings.
        </p>
      </LegalSection>

      <LegalSection number={8} heading="Availability">
        <p>
          We aim to keep HealthPass available, but we do not promise it will be
          uninterrupted. Maintenance, updates and faults happen. The app works
          offline for data already on your device, which is deliberate: your
          emergency card should not depend on our servers being up. We may
          change or discontinue features; if we discontinue a paid feature that
          you are paying for, you may cancel and we refund the unused part of
          the period.
        </p>
      </LegalSection>

      <LegalSection number={9} heading="Liability">
        <p>
          HealthPass is provided as it is. To the fullest extent the law allows,
          we disclaim implied warranties of merchantability, fitness for a
          particular purpose and non-infringement, and we do not warrant that
          the service will be uninterrupted or error-free, or that the health
          requirements and recommendations it shows are complete or current.
        </p>
        <p>
          To the fullest extent the law allows, we are not liable for indirect,
          incidental, special or consequential damages, and our total liability
          for any claim arising out of these terms is limited to what you paid
          us in the twelve months before the claim arose.
        </p>
        <p>
          Some of this does not apply to you. Nothing here excludes liability
          for death or personal injury caused by our negligence, for fraud, or
          anything else that cannot be excluded by law &mdash; and if you are a
          consumer in the EU, EEA or UK, your mandatory statutory rights,
          including the statutory warranty and liability rules of the country
          you live in, apply regardless of anything in this section.
        </p>
      </LegalSection>

      <LegalSection number={10} heading="Changes to these terms">
        <p>
          We may amend these terms and will give you at least 30 days&rsquo;
          notice by email or in the app. If you do not accept an amendment you
          may cancel before it takes effect; continuing to use the service after
          that counts as acceptance.
        </p>
        <p>
          This version applies at once to accounts created on or after 6 October
          2026. For an account created earlier it applies 30 days after we
          emailed you about it, on the date that email names; until then the
          previous version, which we send on request, still applies.
        </p>
      </LegalSection>

      <LegalSection number={11} heading="Applicable law and jurisdiction">
        <p>
          These terms are governed by the laws of the State of New Mexico and
          the federal laws of the United States, excluding their conflict-of-law
          rules, and the courts of Bernalillo County, New Mexico, have
          jurisdiction.
        </p>
        <p>
          If you are a consumer resident in the EU, the EEA or the UK, this does
          not deprive you of the protection of the mandatory law of the country
          you live in, and you may bring proceedings in the courts there.
        </p>
      </LegalSection>

      <LegalSection number={12} heading="Contact">
        <p>
          Questions about these terms:{" "}
          <a
            className="text-primary-600 hover:underline"
            href={`mailto:${contact.general}`}
          >
            {contact.general}
          </a>
          . Company details are in the Imprint.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}

export default TermsPage;
